/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 4;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * Drops stored IDs that storefront search did not return (e.g. unlisted products).
   * Call with the `#predictive-search-products` node from the recently viewed section response
   * (must include `data-recently-viewed-rendered-ids`).
   * @param {ParentNode | null} scope - Response subtree or the `predictive-search-products` element itself.
   */
  static syncFromRecentlyViewedMarkup(scope) {
    if (!scope) return;

    const el =
      scope instanceof HTMLElement && scope.id === 'predictive-search-products'
        ? scope
        : scope.querySelector('#predictive-search-products');

    if (!(el instanceof HTMLElement)) return;
    if (el.dataset.recentlyViewedRenderedIds === undefined) return;

    const raw = el.dataset.recentlyViewedRenderedIds;
    const ids = raw.length > 0 ? raw.split(',').map((s) => s.trim()).filter(Boolean) : [];
    const next = ids.slice(0, this.#MAX_PRODUCTS);

    if (next.length === 0) {
      localStorage.removeItem(this.#STORAGE_KEY);
      return;
    }

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(next));
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
  }
}
